import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AccountRequestsPanel } from '@/components/admin/AccountRequestsPanel';
import { DistributorSettings } from '@/components/admin/DistributorSettings';
import { OrgSmtpSettings } from '@/components/admin/OrgSmtpSettings';
import { ApiTokensPanel } from '@/components/orgs/ApiTokensPanel';
import { CategoriesPanel } from '@/components/orgs/CategoriesPanel';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { setActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import { cn } from '@/lib/cn';
import {
  type Invitation,
  useCreateInvitation,
  useInvitations,
  useRevokeInvitation,
} from '@/lib/invitations';
import {
  type OrganizationMemberOut,
  useDeleteOrg,
  useLeaveOrg,
  useOrgMembers,
  useRemoveMember,
  useResetOrg,
  useUpdateMemberRole,
  useUpdateOrg,
} from '@/lib/orgs';

const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;

export function OrganizationPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const me = useMe();
  const members = useOrgMembers(orgSlug);
  const updateRole = useUpdateMemberRole(orgSlug);
  const remove = useRemoveMember(orgSlug);
  const leave = useLeaveOrg(orgSlug);

  const myMembership = me.data?.organizations.find((o) => o.slug === orgSlug);
  const myRole = myMembership?.role;
  const isOwner = myRole === 'owner';
  const canManage = myRole === 'owner' || myRole === 'admin';

  if (me.data && !myMembership) {
    return (
      <p className="text-stone-500 dark:text-stone-400">
        You aren't a member of <span className="font-mono">{orgSlug}</span>.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{myMembership?.name}</h1>
        <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
          Members of <span className="font-mono">{orgSlug}</span>
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <table className="w-full text-left text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Joined</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {members.isPending && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-stone-400 dark:text-stone-500">
                  Loading…
                </td>
              </tr>
            )}
            {members.data?.map((m) => (
              <MemberRow
                key={m.user_id}
                member={m}
                isSelf={m.user_id === me.data?.user.id}
                isOwner={isOwner}
                canManage={canManage}
                onChangeRole={(role) =>
                  updateRole.mutate({ userId: m.user_id, role })
                }
                onRemove={() => remove.mutate(m.user_id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {(updateRole.isError || remove.isError) && (
        <p className="text-sm text-red-600">
          {((updateRole.error || remove.error) as Error)?.message}
        </p>
      )}

      {canManage && myMembership && (
        <AccountRequestsPanel scope={{ kind: 'org', orgSlug: orgSlug! }} />
      )}

      {canManage && myMembership && (
        <InvitationsPanel orgSlug={orgSlug!} canInviteOwners={isOwner} />
      )}

      {canManage && myMembership && (
        <OrgSettingsPanel orgSlug={orgSlug!} />
      )}

      {canManage && myMembership && (
        <CategoriesPanel orgSlug={orgSlug!} />
      )}

      {myMembership && <ApiTokensPanel orgSlug={orgSlug!} />}

      {canManage && myMembership && (
        <DistributorSettings orgSlug={orgSlug!} />
      )}

      {canManage && myMembership && (
        <OrgSmtpSettings orgSlug={orgSlug!} />
      )}

      {isOwner && myMembership && (
        <ResetZone orgSlug={orgSlug!} />
      )}

      {isOwner && myMembership && (
        <DangerZone orgSlug={orgSlug!} orgName={myMembership.name} />
      )}

      {myMembership && (
        <div className="rounded-md border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4">
          <h2 className="font-medium">Leave this organization</h2>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
            You'll lose access to its parts, BOMs, and inventory. If you're the
            only owner, promote another owner first. If this is your only org,
            create or join another before leaving.
          </p>
          <Button
            variant="danger"
            className="mt-3"
            onClick={() => {
              if (confirm(`Leave ${myMembership.name}?`)) {
                leave.mutate();
              }
            }}
            disabled={leave.isPending}
          >
            {leave.isPending ? 'Leaving…' : 'Leave organization'}
          </Button>
          {leave.isError && (
            <p className="mt-2 text-sm text-red-600">
              {(leave.error as Error).message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function InvitationsPanel({
  orgSlug,
  canInviteOwners,
}: {
  orgSlug: string;
  canInviteOwners: boolean;
}) {
  const list = useInvitations(orgSlug);
  const create = useCreateInvitation(orgSlug);
  const revoke = useRevokeInvitation(orgSlug);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof ROLES)[number]>('member');

  const onInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) return;
    try {
      await create.mutateAsync({ email: email.trim(), role });
      setEmail('');
      setRole('member');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
      <div className="border-b border-stone-200 dark:border-stone-800 p-4">
        <h2 className="text-lg font-semibold">Invite a member</h2>
        <p className="text-sm text-stone-500 dark:text-stone-400">
          Sends an email with a link. The invitee can accept whether or not
          self-registration is enabled.
        </p>
        <form onSubmit={onInvite} className="mt-3 flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[220px]">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Email
            </span>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Role
            </span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as (typeof ROLES)[number])}
              className="block rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
            >
              {ROLES.filter((r) => canInviteOwners || r !== 'owner').map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={create.isPending || !email.trim()}>
            {create.isPending ? 'Sending…' : 'Send invite'}
          </Button>
        </form>
        {create.isError && (
          <p className="mt-2 text-sm text-red-600">
            {(create.error as Error).message}
          </p>
        )}
      </div>

      <div>
        <h3 className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Pending invitations
        </h3>
        {list.data && list.data.length === 0 && (
          <p className="px-4 pb-3 text-sm text-stone-500 dark:text-stone-400">None.</p>
        )}
        {list.data && list.data.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Link</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((inv) => (
                <InvitationRow
                  key={inv.id}
                  invitation={inv}
                  onRevoke={() => revoke.mutate(inv.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function InvitationRow({
  invitation,
  onRevoke,
}: {
  invitation: Invitation;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.accept_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard might be blocked; fall through */
    }
  };
  return (
    <tr className="border-t border-stone-100 dark:border-stone-800">
      <td className="px-3 py-2">{invitation.email}</td>
      <td className="px-3 py-2">{invitation.role}</td>
      <td className="px-3 py-2 text-xs text-stone-500 dark:text-stone-400">
        {new Date(invitation.expires_at).toLocaleString()}
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={onCopy}
          className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
        >
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={onRevoke}
          className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600"
        >
          Revoke
        </button>
      </td>
    </tr>
  );
}

function MemberRow({
  member,
  isSelf,
  isOwner,
  canManage,
  onChangeRole,
  onRemove,
}: {
  member: OrganizationMemberOut;
  isSelf: boolean;
  isOwner: boolean;
  canManage: boolean;
  onChangeRole: (role: string) => void;
  onRemove: () => void;
}) {
  const canEditRow =
    canManage &&
    !isSelf &&
    // Admins can't manage owners.
    !(member.role === 'owner' && !isOwner);

  return (
    <tr className={cn('border-t border-stone-100 dark:border-stone-800', !member.is_active && 'bg-stone-50 dark:bg-stone-900')}>
      <td className="px-3 py-2">{member.email}</td>
      <td className="px-3 py-2 text-stone-600 dark:text-stone-400">{member.name ?? '—'}</td>
      <td className="px-3 py-2">
        {isOwner && !isSelf ? (
          <select
            value={member.role}
            onChange={(e) => onChangeRole(e.target.value)}
            className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm">{member.role}</span>
        )}
      </td>
      <td className="px-3 py-2 text-stone-500 dark:text-stone-400">
        {new Date(member.joined_at).toLocaleDateString()}
      </td>
      <td className="px-3 py-2 text-right">
        {canEditRow && (
          <button
            type="button"
            onClick={() => {
              if (confirm(`Remove ${member.email} from this org?`)) {
                onRemove();
              }
            }}
            className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600"
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}

function OrgSettingsPanel({ orgSlug }: { orgSlug: string }) {
  const me = useMe();
  const update = useUpdateOrg(orgSlug);
  const current = me.data?.organizations.find((o) => o.slug === orgSlug);

  const [name, setName] = useState(current?.name ?? '');
  const [prefix, setPrefix] = useState('');
  const [preferDetailed, setPreferDetailed] = useState(true);

  useEffect(() => {
    if (current) setName(current.name);
  }, [current?.name]);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/orgs/${orgSlug}`, { credentials: 'include' })
      .then((r) => r.json())
      .then(
        (data: {
          part_number_prefix?: string;
          prefer_detailed_description?: boolean;
        }) => {
          if (cancelled) return;
          if (data.part_number_prefix) setPrefix(data.part_number_prefix);
          if (typeof data.prefer_detailed_description === 'boolean') {
            setPreferDetailed(data.prefer_detailed_description);
          }
        },
      )
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  const dirty =
    (current && name.trim() !== current.name) ||
    (prefix && prefix !== prefix.toUpperCase());

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Organization settings</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Owners and admins can rename the org and adjust the auto-assigned
        part-number prefix. Existing parts keep their numbers; only future
        creations use the new prefix.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate({
            name: name.trim() || undefined,
            part_number_prefix: prefix.trim() || undefined,
            prefer_detailed_description: preferDetailed,
          });
        }}
        className="mt-4 grid gap-3 md:grid-cols-2"
      >
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Part-number prefix
          </span>
          <Input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.toUpperCase())}
            maxLength={16}
            className="w-32 font-mono"
          />
        </label>

        <label className="md:col-span-2 flex items-start gap-3 rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950 p-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={preferDetailed}
            onChange={(e) => setPreferDetailed(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-medium">
              Prefer detailed vendor descriptions
            </span>
            <span className="mt-0.5 block text-stone-600 dark:text-stone-400">
              When DigiKey returns both a terse and a long-form description
              ("RES SMD 10K OHM 1% 1/10W 0603" vs "10 kOhms ±1% 0.1W, 1/10W
              Chip Resistor 0603 (1608 Metric)"), use the long form when
              creating or refreshing parts. LCSC and Mouser only expose
              one description, so this only affects DigiKey lookups.
            </span>
          </span>
        </label>

        {update.isError && (
          <p className="md:col-span-2 text-sm text-red-600">
            {(update.error as Error).message}
          </p>
        )}
        {update.isSuccess && !dirty && (
          <p className="md:col-span-2 text-sm text-green-700">Saved.</p>
        )}

        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" disabled={update.isPending || !name.trim()}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function ResetZone({ orgSlug }: { orgSlug: string }) {
  // Wipe every part/BOM/location in this org without losing the org row
  // itself. Useful for testing iterations where re-importing data is
  // faster than chasing down each leftover row from the previous run.
  const reset = useResetOrg(orgSlug);
  const [confirmText, setConfirmText] = useState('');
  const [lastResult, setLastResult] = useState<{
    boms: number;
    parts: number;
    locations: number;
  } | null>(null);

  const onReset = async () => {
    if (confirmText !== orgSlug) return;
    if (
      !confirm(
        `Reset "${orgSlug}"? Every part, BOM, supplier link, location, and uploaded image goes — the org and members stay.`,
      )
    ) {
      return;
    }
    try {
      const result = await reset.mutateAsync(orgSlug);
      setLastResult({
        boms: result.deleted_boms,
        parts: result.deleted_parts,
        locations: result.deleted_locations,
      });
      setConfirmText('');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/40 p-5">
      <h2 className="text-lg font-semibold text-amber-800">Reset organization</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Hard-deletes every part, BOM, supplier link, location, inventory row,
        and uploaded image in this org, and resets the part-number counter
        to <span className="font-mono">10000</span>. The organization, its
        members, and pending invites stay. Useful when you want to re-import
        a clean dataset without spinning up a new org.
      </p>
      <div className="mt-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Type the org slug to confirm:{' '}
            <span className="font-mono text-stone-800 dark:text-stone-200">{orgSlug}</span>
          </span>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="max-w-sm"
          />
        </label>
        <Button
          variant="danger"
          className="mt-3"
          disabled={confirmText !== orgSlug || reset.isPending}
          onClick={onReset}
        >
          {reset.isPending ? 'Resetting…' : 'Reset organization data'}
        </Button>
        {reset.isError && (
          <p className="mt-2 text-sm text-red-600">
            {(reset.error as Error).message}
          </p>
        )}
        {lastResult && !reset.isError && (
          <p className="mt-2 text-sm text-green-700">
            Wiped {lastResult.parts} part{lastResult.parts === 1 ? '' : 's'},{' '}
            {lastResult.boms} BOM{lastResult.boms === 1 ? '' : 's'}, and{' '}
            {lastResult.locations} location
            {lastResult.locations === 1 ? '' : 's'}. Counter reset to AR10000.
          </p>
        )}
      </div>
    </section>
  );
}

function DangerZone({ orgSlug, orgName }: { orgSlug: string; orgName: string }) {
  const navigate = useNavigate();
  const del = useDeleteOrg(orgSlug);
  const [confirmText, setConfirmText] = useState('');

  const onDelete = async () => {
    if (confirmText !== orgName) return;
    if (!confirm(`Last chance — really delete "${orgName}" and all of its parts/BOMs/inventory?`)) {
      return;
    }
    try {
      await del.mutateAsync();
      // Drop the active-org slug; the dashboard picks the user's
      // remaining org (one always exists since the backend blocks
      // deleting your only org).
      setActiveOrgSlug(null);
      navigate('/');
    } catch {
      /* surfaced below */
    }
  };

  return (
    <section className="rounded-lg border border-red-200 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold text-red-700">Delete organization</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Hard delete. Removes every part, BOM, supplier link, location, and
        pending invitation tied to this org. This is irreversible.
      </p>
      <div className="mt-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
            Type the org name to confirm:{' '}
            <span className="font-mono text-stone-800 dark:text-stone-200">{orgName}</span>
          </span>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="max-w-sm"
          />
        </label>
        <Button
          variant="danger"
          className="mt-3"
          disabled={confirmText !== orgName || del.isPending}
          onClick={onDelete}
        >
          {del.isPending ? 'Deleting…' : 'Delete organization'}
        </Button>
        {del.isError && (
          <p className="mt-2 text-sm text-red-600">{(del.error as Error).message}</p>
        )}
      </div>
    </section>
  );
}
